import styled from 'styled-components/native';
import TextGeneric from '../../textGeneric';
import {useTheme} from 'styled-components';
import {ChatBubbleProps} from '../../../interfaces/components/chatBubble';
import {Theme} from '../../../theme/lightTheme';
import RefreshChatBubble from '../refreshChatBubble';
import ProgressiveImage from '../../progressiveImage';
import LoadingDots from '../../loadingDots';
import {formatBubbleMessageDate} from '../../../utils/chat';
import {FlatList, Platform, TouchableOpacity, View, Share} from 'react-native';
import React, {useMemo, memo} from 'react';
import Icon from '../../icon';
import {MessageStatus} from '../../../utils/enums';
import StreamingWordTypeWriter from '../../streamingWordTypeWriter';
import {markdownStyle} from '../../../utils/markdownHelpers';
import {getFontFamily} from '../../../utils/helpers';
import {tr} from '../../../translations';
import useResponsiveDimensions from '../../../utils/hooks/useResponsiveDimensions';
import {isWeb} from '../../../utils/responsive';
import {copyToClipboard} from '../../../utils/helpers';
import MyPressable from '../../myPressable';
import type {
  MessageItem,
  LinksMetaData,
} from '../../../interfaces/reducers/chat';
import ReferencesCarousel from '../../referencesCarousel';
import StreamingLoadingIndicator from '../../streamingLoadingIndicator';

const Container = styled(isWeb ? View : TouchableOpacity)<{selected?: boolean}>`
  background-color: ${({theme, selected}) =>
    selected ? theme.chatBubble.selected : 'transparent'};
`;
const ContentContainer = styled.View<{
  isError?: boolean;
  isSend?: boolean;
  hideArrow?: boolean;
}>`
  background-color: ${({theme, isError, isSend, hideArrow}) =>
    isSend
      ? isError
        ? theme.chatBubble.error
        : theme.chatBubble.backgroundSend
      : theme.chatBubble.backgroundReceived + (hideArrow ? 'FF' : '00')};
  padding: ${({theme, isSend}) =>
    isSend
      ? `${theme.text.s10}px ${theme.text.s8}px`
      : `${theme.text.s12}px ${theme.text.s10}px`};
  max-width: ${({isSend, hideArrow}) => (isSend ? 85 : hideArrow ? 90 : 100)}%;
  min-width: ${({isSend}) => (isSend ? '20%' : 'auto')};
  align-self: ${({isSend, hideArrow}) =>
    hideArrow ? 'center' : isSend ? 'flex-end' : 'flex-start'};
  border-top-left-radius: 20px;
  border-top-right-radius: ${({hideArrow}) => (hideArrow ? 20 : 5)}px;
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
  ${({hideArrow}) => isWeb && hideArrow && `margin:0 auto;`}
  ${({isSend}) =>
    isSend &&
    `
    flex-shrink: 1;
    width: auto;
  `}
`;
const Row = styled.View<{isSend?: boolean}>`
  flex-direction: row;
  align-self: ${({isSend}) => (isSend ? 'flex-end' : 'flex-start')};
  justify-content: ${({isSend}) => (isSend ? 'flex-end' : 'flex-start')};
  width: 100%;
`;
const RowBottom = styled.View<{isSend?: boolean}>`
  flex-direction: row;
  align-self: ${({isSend}) => (isSend ? 'flex-end' : 'flex-start')};
  justify-content: ${({isSend}) => (isSend ? 'flex-end' : 'flex-start')};
  padding-top: ${({theme}) => theme.text.s8}px;
  padding-horizontal: 0px;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  gap: 8px;
`;
const RefreshButton = styled(RefreshChatBubble)`
  padding-horizontal: ${({theme}) => theme.text.s10}px;
  z-index: 2;
`;
const LoadingContainer = styled.View`
  height: ${({theme}) => theme.text.s4}px;
  justify-content: center;
  z-index: 2;
  flex-direction: row;
  align-items: center;
`;
const DateText = styled(TextGeneric)`
  color: ${({theme}) => theme.chatBubble.date};
  font-size: ${({theme}) => theme.text.s9}px;
  text-align: ${({isSend}) => (isSend ? 'right' : 'left')};
  padding-horizontal: ${({isSend}) => (isSend ? 5 : 7)}px;
  font-weight: 500;
  letter-spacing: 0.2px;
`;
const MessageImageContainer = styled.TouchableOpacity`
  height: 200px;
  border-radius: 8px;
  align-self: center;
  background-color: ${({theme}) => theme.primary + '15'};
  overflow: hidden;
  z-index: 2;
`;
const MessageImage = styled(ProgressiveImage)`
  height: 100%;
  width: 100%;
  background-color: transparent;
`;
const AvatarContainer = styled.View`
  width: ${({theme}) => theme.text.s2}px;
  aspect-ratio: 1;
  border-radius: ${({theme}) => theme.text.s2 / 2}px;
  background-color: ${({theme}) => theme.text.light + 'DD'};
  align-items: center;
  justify-content: center;
  align-self: flex-end;
  margin-right: ${({theme}) => theme.text.s2 / 3}px;
  z-index: 999;
`;
const StyledImagesButton = styled.TouchableOpacity<{isSingleImage: boolean}>`
  width: ${({isSingleImage}) => (isSingleImage ? '100%' : '49%')};
  margin-bottom: 5px;
  margin-top: 1px;
  margin-right: ${({isSingleImage}) => (isSingleImage ? 0 : '2%')};
`;

const StyledImages = styled(ProgressiveImage)`
  width: 100%;
  height: 80px;
  border-radius: 5px;
`;
const ReasoningText = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s8}px;
  color: ${({theme}) => theme.text.dark};
  letter-spacing: 1px;
  padding-right: ${({theme}) => theme.text.s10}px;
`;
const Text = styled(TextGeneric)``;

/**
 * ChatBubble component displays a single chat message. It supports interaction through press and long press,
 * differentiates between sent and received messages through visual cues, and displays the message's timestamp.
 *
 * @param {ChatBubbleProps} props - The props for the ChatBubble component.
 * @returns {React.ReactElement} The ChatBubble component.
 */
const ChatBubble: React.FC<ChatBubbleProps> = ({
  onLongPress,
  onPress,
  onRefreshPress,
  onImagePress,
  images,
  message,
  image,
  date,
  selected = false,
  style = {},
  type = 'send',
  isLoading,
  isError = false,
  avatar,
  name,
  hideArrow,
  isMessageSendingLoading,
  lastItemStatus,
  isStreamMessageLoading,
  onStreamingMessageEndAnimation,
  isLastMessage,
  // Add previous message context for export functionality
  previousMessage,
  // New props for enhanced functionality
  references,
  metadata,
  layoutDirection = 'ltr',
  onSharePress,
  onAddToFavorite,
  isFavorite = false,
  shared = false,
  onReadTextPress,
  isSpeaking = false,
  onDebugButtonPress,
  showDebug = false,
  differentialDiagnosisContext,
  ...restProps // This will capture content and other MessageItem properties from {...item}
}: ChatBubbleProps): React.ReactElement => {
  const theme = useTheme() as Theme;
  const {wdp, isMobile} = useResponsiveDimensions();
  const imagesNum = (images || []).length;

  // Use content if available, fallback to message for backward compatibility
  // content comes from MessageItem via spread props {...item} in parent component
  const displayMessage = (message ||
    (restProps as any).content ||
    '') as string;
  const devMessageId = (restProps as any).dev_message_id;
  const debugId = devMessageId || (metadata as any)?.dev_message_id;
  // Use chat_id for phase 2 detection (always available in prod, unlike dev_message_id)
  const chatId = (restProps as any).chat_id || (metadata as any)?.chat_id;

  // Use layout direction from props
  const isRTL = layoutDirection === 'rtl';

  // Determine loading indicator phase based on what data has arrived
  const getLoadingPhase = (): 1 | 2 | 3 => {
    const hasReferences = references && references.length > 0;
    // Use chat_id for phase detection (available in prod), fallback to dev_message_id (dev only)
    const hasFirstResponse = !!chatId || !!debugId;

    if (hasReferences) return 3; // Phase 3: After references arrive
    if (hasFirstResponse) return 2; // Phase 2: After first API response (chat_id received)
    return 1; // Phase 1: Before first API response
  };

  const loadingPhase = getLoadingPhase();
  // Show loading indicator when there's little/no text and we're in any loading phase
  // Phase 1: isStreamMessageLoading must be true
  // Phase 2-3: Show even if isStreamMessageLoading is false, as long as we have no text yet
  const showLoadingIndicator =
    (!displayMessage || displayMessage.length < 10) &&
    (isStreamMessageLoading || loadingPhase > 1);

  // Checkpoint: Log phase transitions for debugging

  const memoizedMarkdownStyle = useMemo(() => {
    return {
      // Spread your base markdown styles
      ...markdownStyle(theme),

      // Override or add the body style
      body: {
        color:
          type === 'send'
            ? theme.chatBubble.sendText
            : theme.chatBubble.receivedText,
        fontFamily: Platform.OS === 'ios' ? getFontFamily().regular : undefined,
        fontSize: Platform.OS === 'ios' ? theme.text.s6 : theme.text.s6 + 1,
        paddingTop: imagesNum > 0 ? theme.text.s7 : 0,
        zIndex: 2,
      },
    };
  }, [theme]);

  const imagesView = () => {
    return (
      <FlatList
        style={{minWidth: wdp(60), zIndex: 2}}
        data={images || []}
        numColumns={imagesNum > 1 ? 2 : 1}
        renderItem={({item, index}) => (
          <StyledImagesButton
            isSingleImage={
              imagesNum === 1 ||
              (imagesNum % 2 !== 0 && imagesNum - 1 === index)
            }
            onPress={() => onImagePress && onImagePress(item)}>
            <StyledImages source={{uri: item?.image_url}} />
          </StyledImagesButton>
        )}
      />
    );
  };

  /**
   * Returns the appropriate icon based on the message status.
   *
   * @returns {string} - The icon name based on the status.
   */
  const getTickIconName = (): string => {
    switch (
      isError || isMessageSendingLoading
        ? MessageStatus.NotSent
        : lastItemStatus
    ) {
      case MessageStatus.NotSent:
        return 'alarm-check';
      case MessageStatus.SentNotReceived:
        return 'check';
      case MessageStatus.ReceivedNotRead:
        return 'check-all';
      case MessageStatus.ReadByEveryone:
        return 'check-all';
      default:
        return '';
    }
  };

  const tickView = () => {
    const tick = getTickIconName();
    if (!tick || type === 'received') {
      return null;
    }
    return (
      <Icon
        name={tick}
        color={
          lastItemStatus === MessageStatus.ReadByEveryone &&
          tick === 'check-all'
            ? theme.text.success
            : theme.text.greyLight
        }
        size={theme.text.s7}
      />
    );
  };

  /**
   * Formats references into markdown list
   * Only includes references with full_link (excludes genio_url only references)
   * Removes duplicates based on full_link
   */
  const formatReferencesAsMarkdown = (): string => {
    if (!references || references.length === 0) {
      return '';
    }

    // Track seen URLs to avoid duplicates
    const seenUrls = new Set<string>();

    // Filter and format references
    const formattedRefs = references
      .map((ref, index) => {
        // Handle string references
        if (typeof ref === 'string') {
          return null; // Skip string-only references as they don't have full_link
        }

        // Only include references with full_link
        const fullLink = ref.full_link;
        if (!fullLink) {
          return null;
        }

        // Skip duplicates
        if (seenUrls.has(fullLink)) {
          return null;
        }
        seenUrls.add(fullLink);

        // Get title from various possible sources
        const title =
          ref.title ||
          ref.website_name ||
          (() => {
            try {
              return new URL(fullLink).hostname.replace(/^www\./, '');
            } catch {
              return fullLink;
            }
          })();

        return `* [${title}](${fullLink})`;
      })
      .filter(Boolean);

    if (formattedRefs.length === 0) {
      return '';
    }

    return `\n\n### References:\n${formattedRefs.join('\n')}`;
  };

  // Message options buttons (Copy, Share, Export, etc.) - only show when assistant message is done streaming
  const messageOptions = () => {
    if (type === 'send') {
      return null;
    }
    // Hide options during streaming, loading, or error states
    if (!displayMessage || isLoading || isStreamMessageLoading || isError) {
      return null;
    }

    // Prepare messages for export - include both user question and AI answer
    // Only prepare export for AI messages since we need the user question context
    const messagesForExport: Array<{
      message: string;
      created_at: string;
      role: string;
    }> = [];

    if (type === 'received') {
      // Add previous message (user question) if it exists
      if (previousMessage && previousMessage.message) {
        messagesForExport.push({
          message: previousMessage.message,
          created_at: previousMessage.created_at || '',
          role: 'user',
        });
      }

      // Add current message (AI answer)
      messagesForExport.push({
        message: displayMessage,
        created_at: date || '',
        role: 'assistant',
      });
    }

    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 6,
          marginLeft: !isMobile ? theme.text.s4 : 0,
          flexWrap: 'wrap',
          paddingVertical: !isMobile ? theme.text.s1 : 4,
        }}>
        {/* Text-to-Speech Button - only for AI messages */}
        {onReadTextPress && !shared && type === 'received' && (
          <MyPressable
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              backgroundColor: 'rgba(51, 51, 51, 0.05)',
              borderRadius: !isMobile ? 20 : 50,
              paddingVertical: !isMobile ? 8 : 10,
              paddingHorizontal: !isMobile ? 12 : 10,
              minHeight: !isMobile ? 36 : 40,
              minWidth: !isMobile ? 'auto' : 40,
              justifyContent: 'center',
              ...(Platform.OS === 'web' && {
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                ':hover': {
                  backgroundColor: 'rgba(51, 51, 51, 0.1)',
                  transform: 'translateY(-1px)',
                },
              }),
            }}
            onPress={() =>
              onReadTextPress({
                ...previousMessage,
                message: displayMessage,
                created_at: date,
              } as MessageItem)
            }
            borderRadius={16}
            iosOpacityEffect>
            <Icon
              name={isSpeaking ? 'pause2' : 'play2'}
              size={theme.text.s5}
              color={theme.text.dark}
            />
            {!isMobile && (
              <TextGeneric
                style={{
                  marginLeft: 6,
                  color: theme.text.dark,
                  fontSize: theme.text.s8,
                  fontWeight: '600',
                  letterSpacing: 0.3,
                }}>
                {isSpeaking ? 'Stop' : 'Play'}
              </TextGeneric>
            )}
          </MyPressable>
        )}

        {/* Copy Button - show for all messages */}
        <MyPressable
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            backgroundColor: 'rgba(51, 51, 51, 0.05)',
            borderRadius: !isMobile ? 20 : 50,
            paddingVertical: !isMobile ? 8 : 10,
            paddingHorizontal: !isMobile ? 12 : 10,
            minHeight: !isMobile ? 36 : 40,
            minWidth: !isMobile ? 'auto' : 40,
            justifyContent: 'center',
            ...(Platform.OS === 'web' && {
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              ':hover': {
                backgroundColor: 'rgba(51, 51, 51, 0.1)',
                transform: 'translateY(-1px)',
              },
            }),
          }}
          onPress={() => {
            const contentToCopy = displayMessage + formatReferencesAsMarkdown();
            copyToClipboard(contentToCopy);
          }}
          borderRadius={16}
          iosOpacityEffect>
          <Icon
            name="copyMessage"
            size={theme.text.s5}
            color={theme.text.dark}
          />
          {!isMobile && (
            <TextGeneric
              style={{
                marginLeft: 6,
                color: theme.text.dark,
                fontSize: theme.text.s8,
                fontWeight: '600',
                letterSpacing: 0.3,
              }}>
              Copy
            </TextGeneric>
          )}
        </MyPressable>

        {/* Share Button - always shares entire conversation, not in shared mode 
          <MyPressable
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              backgroundColor: 'rgba(51, 51, 51, 0.05)',
              borderRadius: 20,
              paddingVertical: 8,
              paddingHorizontal: 12,
              minHeight: Platform.OS === 'web' ? 36 : 32,
              ...(Platform.OS === 'web' && {
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                ':hover': {
                  backgroundColor: 'rgba(51, 51, 51, 0.1)',
                  transform: 'translateY(-1px)',
                },
              }),
            }}
            onPress={onSharePress}
            borderRadius={16}
            iosOpacityEffect>
            <Icon
              name="chatShare"
              size={Platform.OS === 'web' ? theme.text.s5 : theme.text.s6}
              color={theme.text.dark}
            />
            <TextGeneric
              style={{
                marginLeft: 6,
                color: theme.text.dark,
                fontSize:
                  Platform.OS === 'web' ? theme.text.s8 : theme.text.s9,
                fontWeight: '600',
                letterSpacing: 0.3,
              }}>
              Share
            </TextGeneric>
          </MyPressable>
        )}*/}

        {/* Export Button - only for AI messages and not RTL */}
        {type === 'received' && !isRTL && (
          <MyPressable
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              backgroundColor: 'rgba(51, 51, 51, 0.05)',
              borderRadius: !isMobile ? 20 : 50,
              paddingVertical: !isMobile ? 8 : 10,
              paddingHorizontal: !isMobile ? 12 : 10,
              minHeight: !isMobile ? 36 : 40,
              minWidth: !isMobile ? 'auto' : 40,
              justifyContent: 'center',
              ...(Platform.OS === 'web' && {
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                ':hover': {
                  backgroundColor: 'rgba(51, 51, 51, 0.1)',
                  transform: 'translateY(-1px)',
                },
              }),
            }}
            onPress={() => {
              // Import the export function and export both messages
              import('../../../utils/pdf').then(({exportChatToPdf}) => {
                exportChatToPdf(messagesForExport);
              });
            }}
            borderRadius={16}
            iosOpacityEffect>
            <Icon
              name="exportPdf"
              size={theme.text.s5}
              color={theme.text.dark}
            />
            {!isMobile && (
              <TextGeneric
                style={{
                  marginLeft: 6,
                  color: theme.text.dark,
                  fontSize: theme.text.s8,
                  fontWeight: '600',
                  letterSpacing: 0.3,
                }}>
                Export
              </TextGeneric>
            )}
          </MyPressable>
        )}

        {/* Add to Favorite Button - only for AI messages and not in shared mode */}
        {!shared && onAddToFavorite && type === 'received' && (
          <MyPressable
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              backgroundColor: 'rgba(51, 51, 51, 0.05)',
              borderRadius: !isMobile ? 20 : 50,
              paddingVertical: !isMobile ? 8 : 10,
              paddingHorizontal: !isMobile ? 12 : 10,
              minHeight: !isMobile ? 36 : 40,
              minWidth: !isMobile ? 'auto' : 40,
              justifyContent: 'center',
              ...(Platform.OS === 'web' && {
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                ':hover': {
                  backgroundColor: 'rgba(51, 51, 51, 0.1)',
                  transform: 'translateY(-1px)',
                },
              }),
            }}
            onPress={onAddToFavorite}
            borderRadius={16}
            iosOpacityEffect>
            <Icon
              name={isFavorite ? 'star' : 'starOutline'}
              size={theme.text.s5}
              color={theme.text.dark}
            />
            {!isMobile && (
              <TextGeneric
                style={{
                  marginLeft: 6,
                  color: theme.text.dark,
                  fontSize: theme.text.s8,
                  fontWeight: '600',
                  letterSpacing: 0.3,
                }}>
                {isFavorite ? 'Remove from Favorite' : 'Add to Favorite'}
              </TextGeneric>
            )}
          </MyPressable>
        )}
        {showDebug &&
          onDebugButtonPress &&
          type === 'received' &&
          debugId && (
            <MyPressable
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                backgroundColor: 'rgba(51, 51, 51, 0.05)',
                borderRadius: !isMobile ? 20 : 50,
                paddingVertical: !isMobile ? 8 : 10,
                paddingHorizontal: !isMobile ? 12 : 10,
                minHeight: !isMobile ? 36 : 40,
                minWidth: !isMobile ? 'auto' : 40,
                justifyContent: 'center',
                ...(Platform.OS === 'web' && {
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  ':hover': {
                    backgroundColor: 'rgba(51, 51, 51, 0.1)',
                    transform: 'translateY(-1px)',
                  },
                }),
              }}
              onPress={() =>
                onDebugButtonPress({
                  ...previousMessage,
                  ...(restProps as any),
                  message: displayMessage,
                  created_at: date,
                  dev_message_id: debugId,
                } as MessageItem)
              }
              borderRadius={16}
              iosOpacityEffect>
              <Icon name="alert" size={theme.text.s5} color={theme.text.dark} />
              {!isMobile && (
                <TextGeneric
                  style={{
                    marginLeft: 6,
                    color: theme.text.dark,
                    fontSize: theme.text.s8,
                    fontWeight: '600',
                    letterSpacing: 0.3,
                  }}>
                  Debug
                </TextGeneric>
              )}
            </MyPressable>
          )}
      </View>
    );
  };

  return (
    <Container
      activeOpacity={0.95}
      style={style}
      onPress={onPress}
      onLongPress={onLongPress}
      disabled={!onLongPress && !onPress}
      selected={selected}>
      <Row isSend={type === 'send'}>
        {type !== 'send' && isLoading && (avatar || name) && (
          <AvatarContainer>
            <Icon name={'logo'} size={'55%'} />
          </AvatarContainer>
        )}
        {isError && type === 'send' && (
          <RefreshButton onPress={onRefreshPress} />
        )}

        <ContentContainer
          style={{
            minWidth:
              isLoading && type !== 'send'
                ? 'auto'
                : type === 'send'
                  ? undefined
                  : wdp(40),
          }}
          hideArrow={hideArrow}
          isError={isError}
          isSend={type === 'send'}>
          {isLoading && (
            <LoadingContainer>
              <ReasoningText>{tr('chatComponent.reasoning')}</ReasoningText>
              <LoadingDots
                dots={3}
                colors={Array(4).fill(theme.settings.icon)}
                size={theme.text.s10 / 2}
                bounceHeight={theme.text.s9 / 3}
              />
            </LoadingContainer>
          )}
          {/* Phase 1 & 2: Show loading indicator before carousel */}
          {showLoadingIndicator && loadingPhase < 3 && (
            <StreamingLoadingIndicator phase={loadingPhase} />
          )}
          {image ? (
            <MessageImageContainer
              style={{width: wdp(66)}}
              onPress={() => onImagePress && onImagePress({image})}>
              <MessageImage source={{uri: image}} />
            </MessageImageContainer>
          ) : imagesNum > 0 ? (
            imagesView()
          ) : null}

          {/* Render references carousel if available */}
          {references && references.length > 0 && type === 'received' && (
            <View style={{marginBottom: theme.text.s4}}>
              <ReferencesCarousel
                references={references}
                simulateLoading={true}
                isRTL={isRTL}
              />
            </View>
          )}

          {/* Phase 3: Show loading indicator after carousel */}
          {showLoadingIndicator && loadingPhase === 3 && (
            <StreamingLoadingIndicator phase={loadingPhase} />
          )}

          {!!displayMessage &&
            (hideArrow ? (
              <Text
                style={{
                  color: theme.text.dark,
                }}
                numberOfLines={hideArrow ? 3 : undefined}
                isSend={type === 'send'}
                isExtraPaddingTop={imagesNum > 0}>
                {displayMessage}
              </Text>
            ) : (
              <StreamingWordTypeWriter
                text={displayMessage}
                onAnimationEnd={onStreamingMessageEndAnimation}
                withoutAnimation={!isStreamMessageLoading}
                style={{
                  ...memoizedMarkdownStyle,
                  ...(type === 'send' && {
                    textgroup: {
                      paddingVertical: 0,
                    },
                  }),
                }}
                metadata={metadata as LinksMetaData}
                references={references}
                differentialDiagnosisContext={
                  isLastMessage ? differentialDiagnosisContext : undefined
                }
              />
            ))}
        </ContentContainer>
      </Row>
      {!isStreamMessageLoading && !isLoading && (
        <RowBottom isSend={type === 'send'}>
          {messageOptions()}
          {type !== 'send' && (
            <TextGeneric style={{marginLeft: 'auto'}}>|</TextGeneric>
          )}
          {!!date && (
            <DateText isSend={type === 'send'}>
              {formatBubbleMessageDate(date)}
            </DateText>
          )}
          {tickView()}
        </RowBottom>
      )}
    </Container>
  );
};

// Custom comparison function to prevent unnecessary re-renders
const areEqual = (prevProps: ChatBubbleProps, nextProps: ChatBubbleProps) => {
  // Check for chat_id and dev_message_id in spread props FIRST (for phase switching)
  const prevChatId = (prevProps as any).chat_id;
  const nextChatId = (nextProps as any).chat_id;
  const prevDevMessageId = (prevProps as any).dev_message_id;
  const nextDevMessageId = (nextProps as any).dev_message_id;

  if (prevChatId !== nextChatId) {
    return false;
  }
  if (prevDevMessageId !== nextDevMessageId) {
    return false;
  }

  // Check critical props that should trigger re-render
  const criticalProps = [
    'message',
    'date',
    'isLoading',
    'isError',
    'isStreamMessageLoading',
    'isMessageSendingLoading',
    'lastItemStatus',
    'selected',
    'type',
    'isFavorite',
    'isSpeaking',
  ];

  for (const prop of criticalProps) {
    if (
      prevProps[prop as keyof ChatBubbleProps] !==
      nextProps[prop as keyof ChatBubbleProps]
    ) {
      return false;
    }
  }

  // Check arrays/objects that might cause flickering
  if (JSON.stringify(prevProps.images) !== JSON.stringify(nextProps.images)) {
    return false;
  }

  if (
    JSON.stringify(prevProps.references) !==
    JSON.stringify(nextProps.references)
  ) {
    return false;
  }

  // Check metadata changes (especially dev_message_id for phase switching)
  if (
    JSON.stringify(prevProps.metadata) !== JSON.stringify(nextProps.metadata)
  ) {
    return false;
  }

  // Check differentialDiagnosisContext changes (for checkbox selection)
  const prevContext = prevProps.differentialDiagnosisContext;
  const nextContext = nextProps.differentialDiagnosisContext;
  if (prevContext?.selectedRows?.size !== nextContext?.selectedRows?.size) {
    return false;
  }
  // Also check if the actual selected indices changed
  if (prevContext?.selectedRows && nextContext?.selectedRows) {
    const prevArray = Array.from(prevContext.selectedRows);
    const nextArray = Array.from(nextContext.selectedRows);
    if (JSON.stringify(prevArray) !== JSON.stringify(nextArray)) {
      return false;
    }
  }

  // Don't re-render if only non-critical props changed
  return true;
};

export default React.memo(ChatBubble, areEqual);
